import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Minimize2 } from 'lucide-react';
import { useApp } from '@context/AppContext';

/**
 * FocusModePill — floating "restore chrome" button shown while focus mode
 * is active. Tap it to bring the TopBar and Toolbar back.
 *
 * Also shows a dot with the current drawing colour so users have a reminder
 * of what tool state they're in while drawing with no chrome visible.
 *
 * On native platforms, entering focus mode hides the OS status bar and
 * leaving focus mode restores it, so mobile users reclaim every pixel.
 */
export default function FocusModePill() {
  const { state, dispatch } = useApp();
  const { focusMode } = state.ui;
  const { colour, tool, size } = state.canvas;

  // Drive the native status bar to match focus mode. Dynamic import so the
  // web bundle doesn't pull in the plugin unnecessarily — this is the same
  // pattern revenueCat.js uses.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;

    (async () => {
      try {
        const { StatusBar } = await import('@capacitor/status-bar');
        if (cancelled) return;
        if (focusMode) {
          await StatusBar.hide();
        } else {
          await StatusBar.show();
        }
      } catch (err) {
        console.warn('[FocusMode] Status bar control failed:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [focusMode]);

  // Ensure the status bar is restored if the component unmounts mid-focus
  // (e.g. leaving the room while in focus mode).
  useEffect(() => {
    return () => {
      if (!Capacitor.isNativePlatform()) return;
      (async () => {
        try {
          const { StatusBar } = await import('@capacitor/status-bar');
          await StatusBar.show();
        } catch { /* ignore */ }
      })();
    };
  }, []);

  if (!focusMode) return null;

  const handleRestore = () => dispatch({ type: 'TOGGLE_FOCUS_MODE' });

  return (
    <button
      onClick={handleRestore}
      title="Exit focus mode"
      aria-label="Exit focus mode — show controls"
      className="fixed top-2 right-2 z-40 flex items-center gap-2 px-3 py-2 rounded-full bg-white/90 backdrop-blur-sm shadow-lg border border-slate-200 hover:bg-white transition-colors"
    >
      {/* Current colour/tool indicator */}
      <span
        className="w-4 h-4 rounded-full border border-slate-300 shrink-0"
        style={{ backgroundColor: tool === 'eraser' ? '#FFFFFF' : colour }}
      />
      <span className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
        {tool}{size ? ` · ${size}` : ''}
      </span>
      <Minimize2 className="w-3.5 h-3.5 text-slate-500" />
    </button>
  );
}
